// PageLayout — the shared wrapper for every authenticated page.
//
// Renders:
//   * TopNav at the top
//   * Left Sidebar + main content below
//   * Floating chrome surfaces (KeyboardCheatSheet, StewChatPanel)
//   * NoticeFromQuery to bridge `?notice=` URL params → toast
//
// PageLayout owns ChromeProvider so every authenticated descendant
// participates in chrome state (sidebar collapse, Ask Stew panel,
// cheat sheet open).

import { Suspense, useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useSession } from "../session";
import { ChromeProvider, useChrome } from "./ChromeContext";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { SessionIdleWarning } from "./SessionIdleWarning";
import { ReconnectOffice365Banner } from "./ReconnectOffice365Banner";
import { KeyboardCheatSheet } from "./KeyboardCheatSheet";
import { NoticeFromQuery } from "./NoticeFromQuery";
import { Breadcrumbs } from "./Breadcrumbs";
import { MobileTabBar } from "./MobileTabBar";
import { RouteErrorBoundary, RouteLoadingFallback } from "./RouteContent";
import { Sidebar } from "./Sidebar";
import { StewChatPanelLazyMount } from "./stew/StewChatPanelLazyMount";
import { NotesDock } from "./notes/NotesDock";
import { notesDockTargetForPath } from "./notes/notes-dock-route";
import { DocumentsDock } from "./documents/DocumentsDock";
import { documentsDockTargetForPath } from "./documents/documents-dock-route";
import { DockStateProvider } from "./docks/dock-state";
import { TopNav } from "./TopNav";
import { MarketingHubSwitch } from "./marketing/MarketingHubSwitch";
import { hubLocation, hubPlaces } from "./marketing/hub-places";

interface PageLayoutProps {
  children: ReactNode;
}

// /email/* gets a special main-area treatment — the page renders a
// 2-pane (message list + reading pane) layout that needs to fill the
// available height with no max-width container. The global Sidebar
// stays visible (the EMAIL section there hosts the live folder tree
// — see EmailSidebarSection) so the user can switch surfaces without
// leaving the email context. The flag below controls only main-area
// padding/height, not whether the sidebar renders.
function isEmailMainRoute(pathname: string): boolean {
  return pathname === "/email" || pathname.startsWith("/email/");
}

// The kanban board fills the main area so its columns scroll inside
// themselves (the board scrolls sideways) instead of the whole page
// scrolling. Breadcrumbs + the page header stay visible; only the board
// region below them scrolls. Same height mechanism as the email route,
// but padding is kept.
function isBoardMainRoute(pathname: string): boolean {
  return pathname === "/opportunities/board" || pathname === "/processing/board";
}

// The front-desk dashboard's schedule board wants real desktop width —
// eight advisor columns need room — so this route lifts the shared
// 1440px content cap. Unlike the kanban board route above, the page
// still scrolls normally (no fixed height / overflow lock): the board
// is one section among several on a long dashboard, not the whole page.
function isWideMainRoute(pathname: string): boolean {
  // /ongoing-care carries the renewal stage board: with seven-plus stages
  // the shared 1440px cap forced a sideways scroll on a wide monitor, which
  // made drag-and-drop a relay (Ryan, 2026-10-02: "extend with the window").
  return pathname === "/dashboard/donna" || pathname === "/ongoing-care";
}

// Inner component so we can call useKeyboardShortcuts INSIDE the
// ChromeProvider it consumes.
function PageLayoutInner({ children }: PageLayoutProps) {
  const { user, realUser, signOut } = useSession();
  const location = useLocation();
  const chrome = useChrome();
  useKeyboardShortcuts();

  // Dismiss the mobile nav drawer on any route change — tapping a nav
  // link should both navigate AND close the drawer. closeMobileNav is a
  // stable useCallback so this only fires on real path changes.
  const closeMobileNav = chrome.closeMobileNav;
  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  if (!user) {
    // RequireAuth guards this, but the type system doesn't know. Keep
    // the branch as a silent null so we don't crash on racey sign-out.
    return null;
  }
  // TopNav + user menu show the REAL user so the admin never forgets
  // they're impersonating. Sidebar + main content render from the
  // effective user — that's how the per-user dashboard + role-aware
  // sidebar auto-switch during impersonation.
  const topNavUser = realUser ?? user;
  const emailRoute = isEmailMainRoute(location.pathname);
  const boardRoute = isBoardMainRoute(location.pathname);
  const wideRoute = isWideMainRoute(location.pathname);
  const notesTarget = notesDockTargetForPath(location.pathname);
  const documentsTarget = documentsDockTargetForPath(location.pathname);
  // The Marketing Hub's switch (Today, Reach out, Content, Growth, Results,
  // Settings) sits on every Hub page. On a page the switch names exactly,
  // the switch IS the wayfinding, so the breadcrumb trail steps aside; on a
  // detail page under one (an event, a goal) the trail stays for "back".
  const hubPlacesForUser = hubPlaces(user);
  const hub = hubLocation(hubPlacesForUser, location.pathname, location.search);
  return (
    <div
      className={`page-layout${emailRoute ? " page-layout-email-main" : ""}${
        boardRoute ? " page-layout-board-main" : ""
      }${wideRoute ? " page-layout-wide-main" : ""}`}
    >
      <ImpersonationBanner />
      <TopNav user={topNavUser} onSignOut={signOut} />
      {/* App-wide "Office 365 disconnected — reconnect" alert. Self-hides
          unless the signed-in user's Microsoft Graph connection is in a
          broken state (expired / error / subscription_lost) — a dropped
          connection breaks email + calendar + tasks everywhere, so it lives
          in the shared chrome rather than any one dashboard. */}
      <ReconnectOffice365Banner />
      {/* "You'll be signed out soon" notice. Chrome, not a page concern:
          the point is that it reaches you wherever you happen to be
          sitting when the idle window runs low. */}
      <SessionIdleWarning />
      <NoticeFromQuery />
      <div className="page-layout-body">
        <Sidebar user={user} />
        {/* The Documents tab is fixed to the right edge of the viewport at
            mid-height, so on a window where the content column reaches that
            edge it paints over the last few pixels of the page (Cambria,
            2026-09-09 — it sat on top of the Stewardship Financial card).
            Reserve its strip, but only on the routes where it actually
            mounts, so every other page keeps its full width. */}
        <main
          id="main-content"
          className={`page-layout-main${documentsTarget ? " page-layout-main--docs-dock" : ""}`}
          tabIndex={-1}
        >
          {!emailRoute && !hub?.exact && <Breadcrumbs pathname={location.pathname} user={user} />}
          {hub && <MarketingHubSwitch places={hubPlacesForUser} place={hub.place} page={hub.page} />}
          {/* Mylah's walkthrough fix (2026-07-01): the lazy page loads
              (and any render crash) resolve INSIDE the chrome, so the
              nav stays clickable while a page loads, stalls, or fails —
              instead of the full-screen boot skeleton with no way out.
              See RouteContent.tsx for the whole story. */}
          <RouteErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
      {/* Phone bottom tab bar (Wave 0 slice 1) — CSS-hidden above 768px.
          Renders from the EFFECTIVE user (same as Sidebar) so the bar's
          role-aware work slot switches during impersonation. */}
      <MobileTabBar user={user} />
      {chrome.isMobileNavOpen && (
        <button
          type="button"
          className="mobile-nav-scrim"
          aria-label="Close navigation menu"
          onClick={chrome.closeMobileNav}
          tabIndex={-1}
        />
      )}
      <KeyboardCheatSheet />
      <StewChatPanelLazyMount />
      {/* Notes, OS-wide (S13). Mounted here rather than on a record shell
          because there ISN'T one shared shell: ModeShell covers
          Opportunity / Processing / Ongoing Care / Service Record, while
          Household and Contact use DetailHeader. This is the only slot
          genuinely common to all six, so mounting once here is what makes
          the dock identical everywhere instead of six near-copies.

          It reads the record from the URL (notes-dock-route.ts) and
          renders nothing anywhere else — the key on the record means a
          fresh dock per record, so no draft can survive onto a different
          household's notes. */}
      {/* One shared "which dock is open" state so Notes (bottom) and
          Documents (right edge) can never both be open — X1, 2026-09-04. */}
      <DockStateProvider>
        {notesTarget && (
          <NotesDock
            key={`${notesTarget.kind}:${notesTarget.entityId}`}
            kind={notesTarget.kind}
            entityId={notesTarget.entityId}
          />
        )}
        {documentsTarget && (
          <DocumentsDock
            key={`docs:${documentsTarget.kind}:${documentsTarget.entityId}`}
            entityType={documentsTarget.entityType}
            entityId={documentsTarget.entityId}
            kind={documentsTarget.kind}
          />
        )}
      </DockStateProvider>
    </div>
  );
}

export function PageLayout({ children }: PageLayoutProps) {
  return (
    <ChromeProvider>
      {/* EmailComposerProvider (Ask Stew's useEmailComposer().openWith(...))
          now mounts in App.tsx, above CleanupProvider — not here. It used
          to be mounted at this level, which left the in-record Cleanup
          drawer (mounted by CleanupProvider as a sibling of the routed
          page, not a descendant of PageLayout) without access to it and
          crashed the app the moment that drawer rendered a person picker
          with an "email this person" button (2026-09-16 Sentry fix). See
          App.tsx for the mount point. Per-page ComposeButton +
          EmailSidebarSection still mount their own composer locally —
          defer their migration to a follow-up. */}
      <PageLayoutInner>{children}</PageLayoutInner>
    </ChromeProvider>
  );
}
